import { useCallback, useState } from 'react';
import { toast } from 'react-hot-toast';
import {
  CreditTransactionDetailModal,
  PortalCreditTransactionsLedger,
  UsdWalletTransactionDetailModal,
  downloadBlob,
  resolveCreditPeriodDateRange,
  usePortalLedgerDetailState,
  validateCreditExportPeriod,
  type CreditLedgerFilterState,
  type CreditLedgerListResponse,
  type CreditLedgerSortField,
  PAGE,
} from '@kioskgaming/ui';
import AppLayout from '../components/layout/AppLayout';
import {
  exportSuperAgentWalletTransactions,
  fetchAgentTransactions,
  fetchSuperAgentCreditTransactionById,
  fetchSuperAgentUsdLedgerEntryById,
} from '../services/api';

export default function AgentTransactionsPage() {
  const [data, setData] = useState<CreditLedgerListResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [hostSort, setHostSort] = useState<{ sort: CreditLedgerSortField; sortDir: 'asc' | 'desc' }>({
    sort: 'created',
    sortDir: 'desc',
  });
  const [sortFetchTick, setSortFetchTick] = useState(0);

  const ledgerDetail = usePortalLedgerDetailState({
    creditRows: data?.items ?? [],
    fetchCreditById: async (id) => {
      const res = await fetchSuperAgentCreditTransactionById(id);
      if (!res.success || !res.data?.item) throw new Error(res.message || 'Not found');
      return res.data.item;
    },
    fetchUsdById: async (id) => {
      const res = await fetchSuperAgentUsdLedgerEntryById(id);
      if (!res.success || !res.data?.item) throw new Error(res.message || 'Not found');
      return res.data.item;
    },
    onUnavailable: () =>
      toast.error('Related transaction is unavailable or outside your permission.'),
  });

  const onFetch = useCallback(
    async ({
      filters,
      page,
      limit,
    }: {
      filters: CreditLedgerFilterState;
      page: number;
      limit: number;
    }) => {
      setLoading(true);
      try {
        const res = await fetchAgentTransactions({
          page,
          limit,
          owner_scope: 'self',
          period: filters.period,
          ...(filters.period === 'custom'
            ? { date_from: filters.dateFrom, date_to: filters.dateTo }
            : {}),
          type: filters.typeFilter,
          search: filters.search.trim() || undefined,
          sort: hostSort.sort,
          sort_dir: hostSort.sortDir,
        });
        if (res.success && res.data) {
          setData({
            items: res.data.items,
            pagination: res.data.pagination,
            summary: res.data.summary,
          });
        } else {
          setData(null);
          toast.error(res.message || 'Could not load transactions');
        }
      } catch {
        setData(null);
        toast.error('Could not load transactions');
      } finally {
        setLoading(false);
      }
    },
    [hostSort],
  );

  const handleExport = async ({ filters }: { filters: CreditLedgerFilterState }) => {
    const dates = resolveCreditPeriodDateRange(filters.period, filters.dateFrom, filters.dateTo);
    const periodErr = validateCreditExportPeriod(dates.dateFrom, dates.dateTo);
    if (periodErr) {
      toast.error(periodErr);
      return;
    }
    setExporting(true);
    try {
      const { blob, filename } = await exportSuperAgentWalletTransactions({
        owner_scope: 'self',
        period: filters.period,
        ...(filters.period === 'custom'
          ? { date_from: dates.dateFrom, date_to: dates.dateTo }
          : {}),
        type: filters.typeFilter,
        search: filters.search.trim() || undefined,
      });
      downloadBlob(blob, filename);
      toast.success('Credit transactions exported');
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  return (
    <AppLayout>
      <PortalCreditTransactionsLedger
        title={PAGE.creditTransactions}
        loading={loading}
        data={data}
        onFetch={onFetch}
        onExport={handleExport}
        exporting={exporting}
        onViewDetail={(row) => ledgerDetail.openCredit(row)}
        description="Review credit wallet activity with filters and detailed drill-down."
        hostSort={{
          sort: hostSort.sort,
          sortDir: hostSort.sortDir,
          onSort: (field) => {
            setHostSort((prev) => ({
              sort: field,
              sortDir: prev.sort === field && prev.sortDir === 'desc' ? 'asc' : 'desc',
            }));
            setSortFetchTick((t) => t + 1);
          },
        }}
        sortFetchTick={sortFetchTick}
      />
      <CreditTransactionDetailModal
        entry={ledgerDetail.creditDetail}
        onClose={ledgerDetail.closeCredit}
        accountLabel="Your Super Agent account"
        onOpenUsdSource={ledgerDetail.handleOpenUsdSource}
        relatedLoading={ledgerDetail.relatedLoading}
        resolveUsdConversion={ledgerDetail.resolveUsdConversion}
      />
      <UsdWalletTransactionDetailModal
        row={ledgerDetail.usdDetail}
        onClose={ledgerDetail.closeUsd}
        onOpenCreditSource={ledgerDetail.handleOpenCreditSource}
        onOpenUsdSource={ledgerDetail.handleOpenUsdSource}
        relatedLoading={ledgerDetail.relatedLoading}
      />
    </AppLayout>
  );
}
